<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'HandoverProtocol',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
    contractId: {
      type: String,
      required: false,
    },
    revisionId: {
      type: String,
      required: false,
    },
    handoverType: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formName = ref('handoverProtocol');
  const formId = ref(null);
  const labelAlign = ref('left');
  const handoverTypes = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    handoverTypes: handoverTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = () => {
    return props.oid
      ? $fn.value.fetch('read_contract_protocol', {
          asResource: true,
          parameters: { id: props.oid },
        })
      : $fn.value.fetch('get_contract_protocol_template', {
          asResource: true,
          parameters: {
            contract_id: props.contractId,
            revision_id: props.revisionId,
            handover_type: props.handoverType,
          },
        });
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_handoverTypes = () => {
    return $fn.value.getEnumValuesFromSchema('HandoverType');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'handoverTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      handoverTypes: $$load_handoverTypes,
    },
    ux: {},
    dataRefs: {
      item,
      formName,
      formId,
      labelAlign,
      handoverTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext('Contract', 'Handover protocol'),
          $fn.value.pgettext(
            'HandoverType',
            item.value.handover_type,
            'Sentence'
          ),
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.contractId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contractId');
    }
  );
  watch(
    () => props.revisionId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('revisionId');
    }
  );
  watch(
    () => props.handoverType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('handoverType');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ydvptphdx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_closeModal_4svzo1ybb_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_script_3j2legek1_value_callable = async (
    $event
  ) => {
    return item.value.reset($event.data);
  };
  const act_1040167445267876_script_3j2legek1_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_3j2legek1_value_callable($event);
      },
    };
  };
  const inst_evh_1040167445267876_success_ohsunaq4i_getWhen = async (
    $event
  ) => {
    return $event.data;
  };
  const act_1040167445267876_crud_zy8mf7xwg_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_5534025912102772_emit_s6e5inhgm_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_2140198529698299_closeComponent_i2ancegpv_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_crud_guxhldbjz_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: { ...item.value, protocol_status: 'cancelled' },
    };
  };
  const act_2140198529698299_crud_guxhldbjz_getConfirm = async ($event) => {
    return {
      header: 'Jegyőkönyv érvénytelenítése',
      message: 'A jegyzőkönyv érvénytelenítés után már nem módosítható.',
      btnOk: 'Érvénytelenítés',
      severity: 'warning',
    };
  };
  const act_2434514588044777_closeComponent_al03zgqnm_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2434514588044777_crud_yrhpl9cq4_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: { ...item.value, protocol_status: 'completed' },
    };
  };
  const act_2434514588044777_crud_yrhpl9cq4_getConfirm = async ($event) => {
    return {
      header: 'Jegyőkönyv véglegesítése',
      message: 'A jegyzőkönyv véglegesítés után már nem módosítható.',
      btnOk: 'Véglegesítés',
      severity: 'warning',
    };
  };
  const act_2167826738365259_script_f0u4v73jd_value_callable = async (
    $event
  ) => {
    return item.value.reset($event.data);
  };
  const act_2167826738365259_script_f0u4v73jd_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2167826738365259_script_f0u4v73jd_value_callable($event);
      },
    };
  };
  const inst_evh_2167826738365259_success_z59zo1wqc_getWhen = async (
    $event
  ) => {
    return $event.data;
  };
  const act_2167826738365259_crud_4ezq9p6r7_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_protocol',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_7253299514750264_closeComponent_k0dq8ovzr_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };

  const act_7315092382398562_closeModal_ydvptphdx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ydvptphdx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_lyz5wxctx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ydvptphdx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_lyz5wxctx],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_2vhr1bkek =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_60qu2ozlj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2vhr1bkek,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_60qu2ozlj],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_myf6twh4p =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_l7axvhg3d =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_myf6twh4p,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_l7axvhg3d],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_4r8eaxpw7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_jnfoml6is =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_4r8eaxpw7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_jnfoml6is],
    event: 'reload',
  });
  const act_6466705138206980_closeModal_4svzo1ybb =
    new actionTypes.CloseModalAction({
      actionArgs: act_6466705138206980_closeModal_4svzo1ybb_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_r7t6i1ldf =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeModal_4svzo1ybb,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_deleted_r7t6i1ldf],
    event: 'deleted',
  });
  const act_1040167445267876_script_3j2legek1 = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_3j2legek1_getActionArgs,
  });
  const inst_evh_1040167445267876_success_ohsunaq4i =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_3j2legek1,
      event: 'success',
      when: inst_evh_1040167445267876_success_ohsunaq4i_getWhen,
      displayName: 'script',
    });
  const act_1040167445267876_crud_zy8mf7xwg = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_zy8mf7xwg_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_ohsunaq4i],
  });
  const inst_evh_1040167445267876_submit_5f5s0x9op =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_zy8mf7xwg,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_5f5s0x9op],
    event: 'submit',
  });
  const act_5534025912102772_reloadComponentData_ldlzuu1uj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5534025912102772_reload_z45px2369 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadComponentData_ldlzuu1uj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5534025912102772_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_reload_z45px2369],
    event: 'reload',
  });
  const act_5534025912102772_closeComponent_5q4ohrd7q =
    new actionTypes.CloseComponentAction({});
  const inst_evh_5534025912102772_close_ddk75rzct =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeComponent_5q4ohrd7q,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_5534025912102772_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_close_ddk75rzct],
    event: 'close',
  });
  const act_5534025912102772_emit_s6e5inhgm = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_s6e5inhgm_getActionArgs,
  });
  const inst_evh_5534025912102772_changed_4rt5t1yfp =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_s6e5inhgm,
      event: 'changed',
      displayName: 'emit',
    });
  const inst_evh_5534025912102772_changed = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_changed_4rt5t1yfp],
    event: 'changed',
  });
  const act_2140198529698299_closeComponent_i2ancegpv =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2140198529698299_closeComponent_i2ancegpv_getActionArgs,
    });
  const inst_evh_2140198529698299_success_0utxurorl =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_i2ancegpv,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2140198529698299_crud_guxhldbjz = new actionTypes.CRUDAction({
    actionArgs: act_2140198529698299_crud_guxhldbjz_getActionArgs,
    confirm: act_2140198529698299_crud_guxhldbjz_getConfirm,
    displayName: 'cancel',
    events: [inst_evh_2140198529698299_success_0utxurorl],
  });
  const inst_evh_2140198529698299_clickPreventStop_m9uxe6ddw =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_crud_guxhldbjz,
      event: 'click.prevent.stop',
      displayName: 'cancel',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_m9uxe6ddw],
      event: 'click.prevent.stop',
    });
  const act_2434514588044777_closeComponent_al03zgqnm =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2434514588044777_closeComponent_al03zgqnm_getActionArgs,
    });
  const inst_evh_2434514588044777_success_yazhvhkdu =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_al03zgqnm,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2434514588044777_crud_yrhpl9cq4 = new actionTypes.CRUDAction({
    actionArgs: act_2434514588044777_crud_yrhpl9cq4_getActionArgs,
    confirm: act_2434514588044777_crud_yrhpl9cq4_getConfirm,
    displayName: 'complete',
    events: [inst_evh_2434514588044777_success_yazhvhkdu],
  });
  const inst_evh_2434514588044777_clickPreventStop_ad8uy3252 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_crud_yrhpl9cq4,
      event: 'click.prevent.stop',
      displayName: 'complete',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_ad8uy3252],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_script_f0u4v73jd = new actionTypes.ScriptAction({
    actionArgs: act_2167826738365259_script_f0u4v73jd_getActionArgs,
  });
  const inst_evh_2167826738365259_success_z59zo1wqc =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_script_f0u4v73jd,
      event: 'success',
      when: inst_evh_2167826738365259_success_z59zo1wqc_getWhen,
      displayName: 'script',
    });
  const act_2167826738365259_crud_4ezq9p6r7 = new actionTypes.CRUDAction({
    actionArgs: act_2167826738365259_crud_4ezq9p6r7_getActionArgs,
    displayName: 'save',
    events: [inst_evh_2167826738365259_success_z59zo1wqc],
  });
  const inst_evh_2167826738365259_clickPreventStop_13m8gju16 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_crud_4ezq9p6r7,
      event: 'click.prevent.stop',
      displayName: 'save',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_13m8gju16],
      event: 'click.prevent.stop',
    });
  const act_7253299514750264_closeComponent_k0dq8ovzr =
    new actionTypes.CloseComponentAction({
      actionArgs: act_7253299514750264_closeComponent_k0dq8ovzr_getActionArgs,
    });
  const inst_evh_7253299514750264_clickPreventStop_u3fycqftv =
    new actionTypes.EventHandlerImpl({
      action: act_7253299514750264_closeComponent_k0dq8ovzr,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_7253299514750264_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7253299514750264_clickPreventStop_u3fycqftv],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_lyz5wxctx,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_60qu2ozlj,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_l7axvhg3d,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_jnfoml6is,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_r7t6i1ldf,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_ohsunaq4i,
    inst_evh_1040167445267876_submit_5f5s0x9op,
    save: inst_evh_1040167445267876_submit_5f5s0x9op,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_reload_z45px2369,
    inst_evh_5534025912102772_reload,
    inst_evh_5534025912102772_close_ddk75rzct,
    inst_evh_5534025912102772_close,
    inst_evh_5534025912102772_changed_4rt5t1yfp,
    inst_evh_5534025912102772_changed,
    inst_evh_2140198529698299_success_0utxurorl,
    inst_evh_2140198529698299_clickPreventStop_m9uxe6ddw,
    cancel: inst_evh_2140198529698299_clickPreventStop_m9uxe6ddw,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2434514588044777_success_yazhvhkdu,
    inst_evh_2434514588044777_clickPreventStop_ad8uy3252,
    complete: inst_evh_2434514588044777_clickPreventStop_ad8uy3252,
    inst_evh_2434514588044777_clickPreventStop,
    inst_evh_2167826738365259_success_z59zo1wqc,
    inst_evh_2167826738365259_clickPreventStop_13m8gju16,
    inst_evh_2167826738365259_clickPreventStop,
    inst_evh_7253299514750264_clickPreventStop_u3fycqftv,
    inst_evh_7253299514750264_clickPreventStop,
  };

  const evh_7315092382398562_close_lyz5wxctx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_lyz5wxctx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_60qu2ozlj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_60qu2ozlj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_l7axvhg3d = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_l7axvhg3d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_jnfoml6is = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_jnfoml6is.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_r7t6i1ldf = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_r7t6i1ldf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_ohsunaq4i = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_ohsunaq4i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_5f5s0x9op = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_5f5s0x9op.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload_z45px2369 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload_z45px2369.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close_ddk75rzct = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close_ddk75rzct.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed_4rt5t1yfp = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed_4rt5t1yfp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_0utxurorl = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_0utxurorl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_m9uxe6ddw = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_m9uxe6ddw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_yazhvhkdu = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_yazhvhkdu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_ad8uy3252 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_ad8uy3252.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_success_z59zo1wqc = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_success_z59zo1wqc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_13m8gju16 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_13m8gju16.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop_u3fycqftv = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop_u3fycqftv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7253299514750264_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7253299514750264_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="HandoverProtocol"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcContractProtocolHeader
          :protocol="item"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcContractProtocolHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetHandoverProtocol
          :item="item"
          :form="forms[formName]"
          @reload="evh_5534025912102772_reload($event, {})"
          @close="evh_5534025912102772_close($event, {})"
          @changed="evh_5534025912102772_changed($event, {})"
        >
        </FieldsetHandoverProtocol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          variant="warning"
          icon="fa-times"
          :text="pgettext('HandoverProtocol', 'Cancel protocol')"
          :spinning="actions.cancel.isRunning"
          v-if="loaded && !item.isNew && item.protocol_status === 'draft'"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :form="formId"
          variant="success"
          icon="fa-save"
          :text="gettext('Complete')"
          :spinning="actions.complete.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          class="ms-auto"
          v-if="loaded && !item.isNew && item.protocol_status === 'draft'"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :text="gettext('Save')"
          :spinning="actions.save.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          v-if="loaded && item.protocol_status === 'draft'"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          variant="default"
          :text="gettext('Close')"
          :disabled="actions.save.isRunning"
          class="ms-3"
          @click.prevent.stop="
            evh_7253299514750264_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
